import { defineStore } from "pinia";
import { computed, ref } from "vue";
import {
  authService,
  type MeUser,
  type LoginResponse,
} from "@/services/auth";
import { setAccessToken, registerLogoutHandler } from "@/services/api";

export const useAuthStore = defineStore("auth", () => {
  const user = ref<MeUser | null>(null);
  const permissions = ref<string[]>([]);
  const accessExpiresAt = ref<string | null>(null);
  const bootstrapped = ref(false);

  const isAuthenticated = computed(() => user.value !== null);

  function applyLogin(payload: LoginResponse) {
    user.value = payload.user;
    permissions.value = payload.permissions ?? [];
    accessExpiresAt.value = payload.access_expires_at;
    setAccessToken(payload.access_token);
  }

  function clear() {
    user.value = null;
    permissions.value = [];
    accessExpiresAt.value = null;
    setAccessToken(null);
  }

  async function login(email: string, password: string, opts: { orgSlug?: string; totp?: string } = {}) {
    const resp = await authService.login(email, password, opts);
    applyLogin(resp);
    return resp;
  }

  async function logout() {
    // Best-effort: a failed network call doesn't change the user's
    // intent to be logged out. We always clear local state and never
    // surface the network error to the caller.
    try {
      await authService.logout();
    } catch {
      /* swallow */
    } finally {
      clear();
    }
  }

  // Hydrate runs once at app start. If the refresh cookie is valid we end
  // up with a fresh access token + user; otherwise we land logged out.
  async function hydrate() {
    if (bootstrapped.value) return;
    try {
      // /v1/me requires Bearer. We rely on the refresh interceptor: the
      // first /me call returns 401, the interceptor refreshes, then
      // /me retries successfully. Failure means no refresh cookie.
      const me = await authService.me();
      user.value = me.user;
      permissions.value = me.permissions ?? [];
    } catch {
      clear();
    } finally {
      bootstrapped.value = true;
    }
  }

  function hasPermission(code: string): boolean {
    return (
      permissions.value.includes(code) ||
      permissions.value.includes("org:manage_all")
    );
  }

  registerLogoutHandler(clear);

  return {
    user,
    permissions,
    accessExpiresAt,
    bootstrapped,
    isAuthenticated,
    applyLogin,
    clear,
    login,
    logout,
    hydrate,
    hasPermission,
  };
});
